import { inject, Injectable } from '@angular/core';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import {
    ActionDisabledCondition,
    ActionItemOptions,
    MhSelection,
    PersonalSelectionService,
    UserAction
} from '@mediahaven/shared';
import { ApiFragment, Mh2Entity, NavigationLink } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionService } from '../../abstract/action.service.interface';

@Injectable({ providedIn: 'root' })
export class RemoveFromSelectionActionService extends ActionService {
    protected readonly action: MediaHavenActionType = MediaHavenActionType.REMOVE_FROM_SELECTION;

    // Services
    private readonly selectionService: PersonalSelectionService = inject(PersonalSelectionService);
    private readonly dialog: DialogHelper = inject(DialogHelper);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Remove From Selection
     * Available when:
     *  * User has a (personal) selection item
     *  * Item is in this selection
     *  * Item is NOT deleted
     *
     * Disabled when:
     *  * Never
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return this.selectionService.getMhSelection().pipe(
            catchError(error => {
                return of(null);
            }),
            map(selection => {
                return this.userActionBuilder(record, selection);
            })
        );
    }

    public userActionBuilder(record: ApiFragment, selection: MhSelection | null): UserAction {
        const hasSelection: boolean = selection !== null;
        const isInSelection: boolean = selection ? record.isInSelection(selection.fragmentId) : false;

        const isDeleted: boolean = record.isDeleted();

        const availableConditions: boolean[] = [hasSelection, isInSelection, !isDeleted];
        const disableConditions: ActionDisabledCondition[] = [];

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(record: ApiFragment, actionItemOption: ActionItemOptions): Observable<boolean> {
        const message: string = this.getRemoveFromSelectionConfirmationMessage(record);
        const title: string = this.translate('actions.selection.remove');
        const yes: string = this.translate('actions.selection.remove');
        const no: string = this.translate('actions.common.cancel');

        return this.dialog.confirm({ title, message, yes, no }).pipe(
            tap(() => this.initBusyToast(record, actionItemOption, true)),
            switchMap(() => this.selectionService.removeFragmentsFromSelection([record])),
            map(() => true)
        );
    }

    private getRemoveFromSelectionConfirmationMessage(record: ApiFragment): string {
        const entity: Mh2Entity = record.getMh2Entity();
        return this.translate(`items.delete-confirm-dialog.from-selection.${entity}`, { items: 1 });
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.selection.remove';
    }

    public getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.remove-from-selection';
    }

    public getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public getSuccessToastLink(): NavigationLink | undefined {
        return {
            route: `/selection/overview`
        };
    }

    public getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return 'toasts.see.selection';
    }

    public getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }
}
